import { HttpClient } from '@angular/common/http';
import { Component, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { catchError, of } from 'rxjs';
import { ContactTypeEditorComponent } from '../contacts/contact-type-editor/contact-type-editor.component';

type DetailTab = 'details' | 'shipments' | 'barcodes' | 'pallets';

interface DetailStat {
  label: string;
  value: string;
  tone?: 'normal' | 'muted' | 'good' | 'warn';
}

@Component({
  selector: 'app-trip-package-operations-detail',
  templateUrl: './trip-package-operations-detail.component.html',
  styleUrls: ['./trip-package-operations-detail.component.scss']
})
export class TripPackageOperationsDetailComponent implements ICellRendererAngularComp {
  trip: any = {};
  tripListDetails: any = {};
  tripListUuid = '';
  loading = false;
  errorMessage = '';
  activeTab: DetailTab = 'details';
  shipments: any[] = [];
  barcodes: any[] = [];
  pallets: any[] = [];

  readonly tabs: Array<{ key: DetailTab; label: string; icon: string }> = [
    { key: 'details', label: 'Details', icon: 'fa-list-check' },
    { key: 'shipments', label: 'Shipments', icon: 'fa-truck-ramp-box' },
    { key: 'barcodes', label: 'Barcodes', icon: 'fa-barcode' },
    { key: 'pallets', label: 'Pallets', icon: 'fa-pallet' }
  ];

  readonly barcodeStatusTypes = [
    '',
    'MANUAL AUDIT',
    'MISSING',
    'OVERAGE',
    'OPENED',
    'BROKEN',
    'CRUSHED',
    'WET',
    'HOLDOVER'
  ];

  readonly cartonResolutionTypes = [
    '',
    'DELIVERED AS IS',
    'HELD FOR DISPOSITION',
    'RETAPED AND DELIVERED',
    'REPACKAGED AND DELIVERED',
    'DISCARDED DISPOSED'
  ];

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 90,
    cellClass: 'tpod-cell'
  };

  shipmentColumnDefs: ColDef[] = [
    { field: 'shipment_list_uuid', headerName: 'UUID', hide: true },
    { field: 'alternate_reference', headerName: 'FBI No.', width: 115, cellRenderer: (params: any) => this.linkCell(params.value, `/full-order-details/${params.data?.lading_uuid || ''}`) },
    { field: 'asn_number', headerName: 'ASN No.', width: 140 },
    { field: 'trip_number', headerName: 'Trip No.', width: 135 },
    { field: 'status', headerName: 'Status', width: 170, cellRenderer: (params: ICellRendererParams) => this.statusCell(params.value) },
    { field: 'created', headerName: 'Scan', width: 165, valueFormatter: params => this.formatDateTime(params.value) },
    { field: 'total_cartons', headerName: 'Expected', width: 105 },
    { field: 'act_trail_number', headerName: 'ACT Trailer', width: 130 },
    { field: 'external_bill_of_lading', headerName: 'BOL', width: 145 },
    { field: 'store_number', headerName: 'Store', width: 95 }
  ];

  barcodeColumnDefs: ColDef[] = [
    { headerName: '#', width: 68, valueGetter: this.countGetter },
    { field: 'store_number', headerName: 'Store', width: 90 },
    { field: 'barcode', headerName: 'Barcode', width: 230 },
    { field: 'received_in', headerName: 'Received', width: 105, cellRenderer: (params: any) => this.booleanCell(params.value) },
    { field: 'audited', headerName: 'Audited', width: 95, cellRenderer: (params: any) => this.booleanCell(params.value) },
    {
      field: 'return_status',
      headerName: 'Status',
      width: 155,
      editable: true,
      cellEditor: ContactTypeEditorComponent,
      cellEditorPopup: true,
      cellEditorParams: { multiple: false, types: this.barcodeStatusTypes }
    },
    {
      field: 'carton_resolution',
      headerName: 'Resolution',
      width: 190,
      editable: true,
      cellEditor: ContactTypeEditorComponent,
      cellEditorPopup: true,
      cellEditorParams: { multiple: false, types: this.cartonResolutionTypes }
    }
  ];

  palletsColumnDefs: ColDef[] = [
    { field: 'load_no', headerName: 'Load', width: 110 },
    { field: 'store_number', headerName: 'Store', width: 90 },
    { headerName: 'Dims', width: 155, valueGetter: params => this.dimensions(params.data) },
    { field: 'weight', headerName: 'Weight', width: 110 },
    { field: 'cartons_pallet', headerName: 'Cartons', width: 110 }
  ];

  constructor(
    private http: HttpClient,
    private router: Router,
    private zone: NgZone
  ) {}

  agInit(params: ICellRendererParams): void {
    this.trip = params.data || {};
    this.tripListUuid = this.trip.trip_list_uuid || '';
    this.loadDetails();
  }

  refresh(params: ICellRendererParams): boolean {
    this.trip = params.data || this.trip;
    this.tripListUuid = this.trip.trip_list_uuid || this.tripListUuid;
    this.loadDetails();
    return true;
  }

  get customerName(): string {
    return this.firstValue(
      this.trip.customer_display_name,
      this.trip.contact_organization,
      this.trip.customer_name,
      this.trip.customer_contact_organization,
      this.tripListDetails.customer_name,
      this.tripListDetails.contact_organization,
      this.tripListDetails.inventory_warehouse_customer_name
    );
  }

  get detailStats(): DetailStat[] {
    return [
      { label: 'ASN', value: this.value(this.trip.asn_number) },
      { label: 'Trip', value: this.value(this.trip.trip_number) },
      { label: 'Customer', value: this.value(this.customerName), tone: this.customerName ? 'normal' : 'muted' },
      { label: 'Date', value: this.formatDateTime(this.trip.date) },
      { label: 'Created', value: this.formatDateTime(this.trip.created) },
      { label: 'Week', value: this.value(this.trip.week_no) },
      { label: 'BOL', value: this.value(this.trip.external_bill_of_lading) },
      { label: 'ACT Trailer', value: this.value(this.trip.act_trail_number) },
      { label: 'Cartons', value: this.value(this.trip.total_cartons) },
      { label: 'Manifest', value: this.value(this.trip.manifest_carton_count) },
      { label: 'BOL Count', value: this.value(this.trip.bol_carton_count) },
      { label: 'Weight', value: this.trip.reported_weight ? `${this.trip.reported_weight} lbs` : '-' }
    ];
  }

  get rowCounts(): Record<DetailTab, number | null> {
    return {
      details: null,
      shipments: this.shipments.length,
      barcodes: this.barcodes.length,
      pallets: this.pallets.length
    };
  }

  loadDetails(): void {
    if (!this.tripListUuid) {
      this.errorMessage = 'Missing trip UUID.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.http.get<any>('/api/shipment_lists/trip_lists.php', {
      params: {
        name: 'shipment_and_receiving',
        trip_list_uuid: this.tripListUuid,
        command: 'TRIP_DETAILS'
      }
    }).pipe(
      catchError(error => {
        console.error(error);
        this.errorMessage = 'Unable to load trip package details.';
        return of(null);
      })
    ).subscribe(response => {
      this.loading = false;
      if (!response) return;

      const data = response.data || {};
      this.shipments = data.shipment_list || [];
      this.barcodes = data.barcode_list || [];
      this.pallets = data.pallet_list || [];
      this.tripListDetails = data.detail || {};
    });
  }

  selectTab(tab: DetailTab): void {
    this.activeTab = tab;
  }

  openLabels(): void {
    if (!this.tripListUuid) return;
    this.zone.run(() => this.router.navigate(['/trip_package_labels', this.tripListUuid]));
  }

  addPackages(): void {
    if (!this.tripListUuid) return;
    this.zone.run(() => this.router.navigate(['/package_intake'], {
      queryParams: { mode: 'existing_trip', trip_list_uuid: this.tripListUuid }
    }));
  }

  viewFullTrip(): void {
    if (!this.tripListUuid) return;
    this.zone.run(() => this.router.navigate(['/full-trip-details', this.tripListUuid]));
  }

  printTrip(): void {
    if (!this.tripListUuid) return;
    this.zone.run(() => this.router.navigate(['/trip_print', this.tripListUuid]));
  }

  onBarcodeEditingStopped(event: any): void {
    const columnName = event.column.getColId();
    if (columnName !== 'return_status' && columnName !== 'carton_resolution') return;

    const rowData: any = event.node.data || {};
    const productUuid = rowData.product_uuid;
    if (!productUuid) return;

    const url = columnName === 'return_status'
      ? '/api/shipment_lists/shipment_details.php?table=shipment_and_receiving&command=UPDATE_BARCODE_STATUS'
      : '/api/shipment_lists/shipment_details.php?table=shipment_and_receiving&command=PRODUCT_STATUS_UPDATE';

    const payload: any = {
      [columnName]: event.value,
      product_uuid: productUuid
    };

    if (columnName === 'carton_resolution') {
      payload.return_status = rowData.return_status;
    }

    this.http.post<any>(url, payload).subscribe({
      error: error => console.error(error)
    });
  }

  private countGetter(params: ValueGetterParams): number | string {
    return params.node?.rowIndex !== null && params.node?.rowIndex !== undefined ? params.node.rowIndex + 1 : '';
  }

  private linkCell(value: unknown, href: string): HTMLElement {
    const link = document.createElement('a');
    link.textContent = this.value(value);
    link.href = href;
    link.classList.add('tpod-link');
    return link;
  }

  private statusCell(value: unknown): HTMLElement {
    const wrap = document.createElement('div');
    wrap.classList.add('tpod-status-wrap');
    const statuses = String(value || '').split(',').map(status => status.trim()).filter(Boolean);

    if (!statuses.length) {
      const empty = document.createElement('span');
      empty.classList.add('tpod-status', 'is-empty');
      empty.textContent = '-';
      wrap.appendChild(empty);
      return wrap;
    }

    for (const status of statuses) {
      const chip = document.createElement('span');
      chip.classList.add('tpod-status', this.statusTone(status));
      chip.textContent = status;
      wrap.appendChild(chip);
    }

    return wrap;
  }

  private booleanCell(value: unknown): HTMLElement {
    const checked = value === true || value === 'true' || value === 1 || value === '1';
    const wrap = document.createElement('span');
    wrap.classList.add('tpod-bool', checked ? 'is-yes' : 'is-no');
    wrap.innerHTML = checked ? '<i class="fa-solid fa-check"></i>' : '<i class="fa-solid fa-minus"></i>';
    return wrap;
  }

  private statusTone(status: string): string {
    const normalized = status.toLowerCase();
    if (normalized.includes('created')) return 'is-warn';
    if (normalized.includes('completed')) return 'is-good';
    if (normalized.includes('progress')) return 'is-info';
    return 'is-neutral';
  }

  private dimensions(data: any): string {
    if (!data) return '-';
    const parts = [data.length, data.width, data.height].filter(value => value !== null && value !== undefined && value !== '');
    return parts.length ? parts.join(' x ') : '-';
  }

  formatDateTime(value: unknown): string {
    if (!value) return '-';
    const date = new Date(String(value));
    if (Number.isNaN(date.getTime())) return String(value);
    return `${date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })} ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  }

  private value(value: unknown): string {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  }

  private firstValue(...values: unknown[]): string {
    for (const value of values) {
      if (value !== null && value !== undefined && value !== '') return String(value);
    }
    return '';
  }
}
